import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import {
  BuildOutlined as BuildOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TransformOutlined as TransformOutlinedIcon
} from "@mui/icons-material";

import AssistantIcon from "../../icons/AssistantIcon";
import BarChart12Icon from "../../icons/BarChart12Icon";
import BranchDeployerIcon from "../../icons/BranchDeployerIcon";
import CalendarNavIcon from "../../icons/CalendarNavIcon";
import ClockIconMenu from "../../icons/ClockIconMenu";
import CloudCacheIcon from "../../icons/CloudCache";
import Compass03Icon from "../../icons/Compass03Icon";
import ConversationsIcon from "../../icons/ConversationsIcon";
import DashboardSettingsIcon from "../../icons/DashboardSettingsIcon";
import FilePlusIcon from "../../icons/insights/FilePlusIcon";
import FolderIcon from "../../icons/FolderIcon";
import GridIcon from "../../icons/GridIcon";
import InsightsIcon from "../../icons/InsightsIcon";
import IntegrationIcon from "../../icons/IntegrationIcon";
import KnowledgeStudioIcon from "../../icons/KnowledgeStudioIcon";
import LayersThreeIcon from "../../icons/LayersThreeIcon";
import OutboundIcon from "../../icons/OutboundIcon";
import SentimentAnalysisIcon from "../../icons/SentimentAnalysisIcon";
import VoiceOverviewIcon from "../../icons/VoiceOverviewIcon";
import ZapSquareIcon from "../../icons/ZapSquareIcon";
import {
  ACCOUNT_MANAGEMENT,
  ACCOUNT_SETTINGS,
  ALL_ASSISTANTS,
  AUDIT_LOG, BACK_OFFICE,
  BRANCH_DEPLOYER,
  BUILDING_BLOCKS,
  CHAT_INSIGHTS,
  CONVERSATIONS,
  CORE_HYRO_KPIS,
  ENVIRONMENTS,
  HOME,
  INSIGHTS,
  INTEGRATIONS,
  KNOWLEDGE_DEBUGGER,
  KNOWLEDGE_EXPLORER,
  KNOWLEDGE_SOURCES,
  PIPELINE_PERFORMANCE,
  AGENT_PERFORMANCE,
  SYSTEM_OVERRIDES,
  SKILLS,
  VOICE_INSIGHTS,
  CAMPAIGN_MANAGER,
  DATA_PROCESSING,
  CLINIC_AVAILABILITY,
  DRAFTS,
  FAILURES,
  CLOUD_CACHE_UPDATE,
  DATA_MAPPING,
  CONFIGURATION_EDITOR,
  FEATURE_ADOPTION,
  AGENT_SETTINGS,
  DATA_AGENT,
  FEATURE_MANAGEMENT,
  TRACKED_CONFIGS,
  ALL_SUBSCRIPTIONS,
  SENTIMENT_ANALYSIS,
  SKILL_PERFORMANCE,
  VOICE_OVERVIEW
} from "@consts";
import { SKILL_PERFORMANCE_PAGES } from "./NavigationBar.consts";

const CONVERSATIONS_MIN = import.meta.env.VITE_ENV === ENVIRONMENTS.PRODUCTION ? "minute" : "minute";

const filterNavItemByPermission = hasPermission => (navItem) => {
  if (navItem.children) {
    navItem.children = navItem.children.filter(filterNavItemByPermission(hasPermission));
  }
  return navItem.requiredPermission
    ? hasPermission(navItem.requiredPermission)
    : true;
};

const getAssistantNavSections = (accountId, selectedAssistantId) => [
  {
    key: "analytics",
    title: "Analytics",
    items: [
      {
        key: HOME,
        text: "Overview",
        icon: BarChart12Icon,
        url: `/${accountId}/${selectedAssistantId}/${HOME}`,
        requiredPermission: USER_PERMISSIONS.HOME_PAGE.READ
      },
      {
        key: SKILL_PERFORMANCE,
        text: "Skill performance",
        icon: LayersThreeIcon,
        requiredPermission: USER_PERMISSIONS.HOME_PAGE.READ,
        voiceOnly: true,
        excludeOutbound: true,
        children: SKILL_PERFORMANCE_PAGES.map(({ path, text, requiredFlag, requiredBenchmarksSetup }) => ({
          key: `${SKILL_PERFORMANCE}/${path}`,
          text,
          url: `/${accountId}/${selectedAssistantId}/${SKILL_PERFORMANCE}/${path}`,
          requiredFlag,
          requiredBenchmarksSetup
        }))
      },
      {
        key: SENTIMENT_ANALYSIS,
        text: "Sentiment analysis",
        icon: SentimentAnalysisIcon,
        url: `/${accountId}/${selectedAssistantId}/${SENTIMENT_ANALYSIS}`,
        requiredPermission: USER_PERMISSIONS.SENTIMENT_ANALYSIS_PAGE.READ,
        requiredFlag: "enableSentimentAnalysis",
        voiceOnly: true,
        excludeOutbound: true
      },
      {
        key: INSIGHTS,
        text: "Insights",
        description: `Loading data may take time, it refreshes every ${CONVERSATIONS_MIN}.`,
        url: `/${accountId}/${selectedAssistantId}/${INSIGHTS}`,
        icon: InsightsIcon,
        requiredPermission: USER_PERMISSIONS.INSIGHTS_PAGE.READ
      },
      {
        key: CONVERSATIONS,
        text: "Conversations",
        description: `The data is refreshed every ${CONVERSATIONS_MIN}.`,
        icon: ConversationsIcon,
        url: `/${accountId}/${selectedAssistantId}/${CONVERSATIONS}`,
        requiredPermission: USER_PERMISSIONS.CONVERSATIONS_PAGE.READ
      }
    ]
  },
  {
    key: "configuration",
    title: "Configuration",
    items: [
      {
        key: SKILLS,
        text: "Skills",
        icon: ZapSquareIcon,
        url: `/${accountId}/${selectedAssistantId}/${SKILLS}`,
        requiredPermission: USER_PERMISSIONS.SKILLS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: BUILDING_BLOCKS,
        text: "Building blocks",
        icon: GridIcon,
        url: `/${accountId}/${selectedAssistantId}/${BUILDING_BLOCKS}`,
        requiredPermission: USER_PERMISSIONS.BUILDING_BLOCKS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: INTEGRATIONS,
        text: "Integrations",
        icon: IntegrationIcon,
        url: `/${accountId}/${selectedAssistantId}/${INTEGRATIONS}`,
        requiredPermission: USER_PERMISSIONS.INTEGRATIONS_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: KNOWLEDGE_SOURCES,
        text: "Knowledge studio",
        icon: KnowledgeStudioIcon,
        url: `/${accountId}/${selectedAssistantId}/${KNOWLEDGE_SOURCES}`,
        requiredPermission: USER_PERMISSIONS.KNOWLEDGE_SOURCES_PAGE.READ,
        isCodelessRoute: true
      },
      {
        key: CAMPAIGN_MANAGER,
        text: "Campaign manager",
        icon: OutboundIcon,
        url: `/${accountId}/${selectedAssistantId}/${CAMPAIGN_MANAGER}`
      },
      {
        key: CLINIC_AVAILABILITY,
        text: "Clinic availability",
        icon: CalendarNavIcon,
        url: `/${accountId}/${selectedAssistantId}/${CLINIC_AVAILABILITY}`,
        requiredPermission: [
          USER_PERMISSIONS.CLINIC_AVAILABILITY.SUPERVISOR.READ,
          USER_PERMISSIONS.CLINIC_AVAILABILITY.MANAGER.READ
        ],
        requiredFlag: "clinicAvailabilityPage",
        voiceOnly: true
      }
    ]
  },
  {
    key: "hyro-tools",
    title: "Hyro Tools",
    collapsible: true,
    requiredPermission: USER_PERMISSIONS.HYRO_TOOLS_SECTION.READ,
    items: [
      {
        key: AGENT_SETTINGS,
        text: "Agent settings",
        icon: SettingsOutlinedIcon,
        url: `/${accountId}/${selectedAssistantId}/${AGENT_SETTINGS}`
      },
      {
        key: KNOWLEDGE_EXPLORER,
        text: "Knowledge explorer",
        icon: Compass03Icon,
        url: `/${accountId}/${selectedAssistantId}/${KNOWLEDGE_EXPLORER}`,
        requiredPermission: USER_PERMISSIONS.KNOWLEDGE_EXPLORER_PAGE.READ
      },
      {
        key: CLOUD_CACHE_UPDATE,
        text: "Cloud cache editor",
        icon: CloudCacheIcon,
        url: `/${accountId}/${selectedAssistantId}/${CLOUD_CACHE_UPDATE}`,
        requiredPermission: USER_PERMISSIONS.CLOUD_CACHE_EDITOR_PAGE.READ
      },
      {
        key: CONFIGURATION_EDITOR,
        text: "Configuration editor",
        icon: FilePlusIcon,
        url: `/${accountId}/${selectedAssistantId}/${CONFIGURATION_EDITOR}`,
        requiredPermission: USER_PERMISSIONS.CONFIGURATION_EDITOR_PAGE.READ
      },
      {
        key: AUDIT_LOG,
        text: "Audit log",
        icon: ClockIconMenu,
        url: `/${accountId}/${selectedAssistantId}/${AUDIT_LOG}`,
        requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
      }
    ]
  }
];

const getAdminNavItems = () => [
  {
    key: ALL_ASSISTANTS,
    text: "All Agents",
    icon: AssistantIcon,
    url: `/admin/${ALL_ASSISTANTS}`
  },
  {
    key: ACCOUNT_MANAGEMENT,
    text: "Account Management",
    icon: FolderIcon,
    url: `/admin/${ACCOUNT_MANAGEMENT}`
  },
  {
    key: BRANCH_DEPLOYER,
    text: "Branch Deployer",
    icon: BranchDeployerIcon,
    url: `/admin/${BRANCH_DEPLOYER}`,
    requiredPermission: USER_PERMISSIONS.ADMIN_PANEL.MULTI_SERVICES.READ
  },
  {
    key: INSIGHTS,
    text: "Insights",
    icon: InsightsIcon,
    children: [
      {
        key: `${INSIGHTS}/${VOICE_INSIGHTS}`,
        text: "Voice",
        url: `/admin/insights/${VOICE_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${CHAT_INSIGHTS}`,
        text: "Chat",
        url: `/admin/insights/${CHAT_INSIGHTS}`
      },
      {
        key: `${INSIGHTS}/${PIPELINE_PERFORMANCE}`,
        text: "Pipeline Performance",
        url: `/admin/insights/${PIPELINE_PERFORMANCE}`
      },
      {
        key: `${INSIGHTS}/${CORE_HYRO_KPIS}`,
        text: "Core Hyro KPIs",
        url: `/admin/insights/${CORE_HYRO_KPIS}`
      },
      {
        key: `${INSIGHTS}/${AGENT_PERFORMANCE}`,
        text: "Agent Performance",
        url: `/admin/insights/${AGENT_PERFORMANCE}`
      }
    ]
  },
  {
    key: BACK_OFFICE,
    text: "Back Office",
    icon: BuildOutlinedIcon,
    children: [
      {
        key: `${BACK_OFFICE}/${DRAFTS}`,
        text: "Drafts",
        url: `/admin/back-office/${DRAFTS}`
      },
      {
        key: `${BACK_OFFICE}/${FAILURES}`,
        text: "Failures",
        url: `/admin/back-office/${FAILURES}`
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_DEBUGGER}`,
        text: "Debugger",
        url: `/admin/back-office/${KNOWLEDGE_DEBUGGER}`
      },
      {
        key: `${BACK_OFFICE}/${KNOWLEDGE_EXPLORER}`,
        text: "Explorer",
        url: `/admin/back-office/${KNOWLEDGE_EXPLORER}`
      },
      {
        key: `${BACK_OFFICE}/${DATA_MAPPING}`,
        text: "Data Mapping",
        url: `/admin/back-office/${DATA_MAPPING}`,
        requiredPermission: USER_PERMISSIONS.DATA_MAPPING.READ,
      },
      {
        key: `${BACK_OFFICE}/${FEATURE_ADOPTION}`,
        text: "Feature Adoption",
        url: `/admin/back-office/${FEATURE_ADOPTION}`,
        requiredPermission: USER_PERMISSIONS.FEATURE_ADOPTION.READ
      },
      {
        key: `${BACK_OFFICE}/${FEATURE_MANAGEMENT}`,
        text: "Feature Management",
        url: `/admin/back-office/${FEATURE_MANAGEMENT}`,
        requiredPermission: USER_PERMISSIONS.FEATURE_ADOPTION.WRITE
      },
      {
        key: `${BACK_OFFICE}/${TRACKED_CONFIGS}`,
        text: "Tracked Configs",
        url: `/admin/back-office/${TRACKED_CONFIGS}`,
        requiredPermission: USER_PERMISSIONS.TRACKED_CONFIGS.WRITE
      },
      {
        key: `${BACK_OFFICE}/${DATA_AGENT}`,
        text: "Data Agent",
        url: `/admin/back-office/${DATA_AGENT}`
      },
      {
        key: `${BACK_OFFICE}/${ALL_SUBSCRIPTIONS}`,
        text: "All Subscriptions",
        url: `/admin/back-office/${ALL_SUBSCRIPTIONS}`,
        requiredPermission: USER_PERMISSIONS.ALL_SUBSCRIPTIONS.READ
      }
    ]
  },
  {
    key: AUDIT_LOG,
    text: "Audit Log",
    icon: ClockIconMenu,
    url: `/admin/${AUDIT_LOG}`,
    requiredPermission: USER_PERMISSIONS.AUDIT_LOG.READ
  },
  {
    key: SYSTEM_OVERRIDES,
    text: "System Overrides",
    icon: DashboardSettingsIcon,
    url: `/admin/${SYSTEM_OVERRIDES}`
  }
];

const getAccountNavSections = (accountId) => [
  {
    key: "main",
    title: null,
    items: [
      {
        key: ALL_ASSISTANTS,
        text: "All Agents",
        icon: AssistantIcon,
        url: `/${accountId}`,
        requiredPermission: USER_PERMISSIONS.ACCOUNT_ASSISTANTS_PAGE.READ
      },
      {
        key: VOICE_OVERVIEW,
        text: "Voice Overview",
        icon: VoiceOverviewIcon,
        url: `/${accountId}/${VOICE_OVERVIEW}`,
        requiredPermission: USER_PERMISSIONS.HOME_PAGE.READ,
        requiredFlag: "enableAccountOverview"
      }
    ]
  },
  {
    key: "configuration",
    title: "Configuration",
    items: [
      {
        key: CAMPAIGN_MANAGER,
        text: "Campaign Manager",
        icon: OutboundIcon,
        url: `/${accountId}/${CAMPAIGN_MANAGER}`,
        requiredPermission: USER_PERMISSIONS.CAMPAIGN_MANAGER.READ
      },
      {
        key: DATA_PROCESSING,
        text: "Data Processing",
        icon: TransformOutlinedIcon,
        url: `/${accountId}/${DATA_PROCESSING}`,
        requiredPermission: USER_PERMISSIONS.CAMPAIGN_MANAGER.READ,
        requiredFlag: "dataProcessingPage"
      },
      {
        key: ACCOUNT_SETTINGS,
        text: "Account Settings",
        icon: SettingsOutlinedIcon,
        url: `/${accountId}/${ACCOUNT_SETTINGS}`,
        requiredPermission: USER_PERMISSIONS.ACCOUNT_SETTINGS.READ
      }
    ]
  }
];

const getAccountNavItems = (accountId) =>
  getAccountNavSections(accountId).flatMap(section => section.items);

export {
  filterNavItemByPermission,
  getAssistantNavSections,
  getAdminNavItems,
  getAccountNavSections,
  getAccountNavItems
};
